import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useState } from "react";
import { TextField, Label } from "react-aria-components";
import { Loader2, TriangleAlert } from "lucide-react";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useShowError } from "../../error/ErrorProvider.js";
import { useManualStats, useDeleteManual, type ManualMutationError } from "./useManualMutations.js";

export interface DeleteManualDialogProps {
  /** The manual to delete. `name` is the (possibly stale) card name — the dialog confirms against the server's `stats.name`. */
  manual: { id: number; name: string };
  session: AuthSession | null;
  onClose: () => void;
  onDeleted: (name: string) => void;
}

/**
 * Hard-delete confirmation for a manual (ADR-012, RED-ZONE endpoint) — mirrors {@link DeleteFacilityDialog} for a single
 * row: fetch the `stats` snapshot (fresh each open — `gcTime: 0`), show name · state · category, and require **typing the
 * manual's exact name** before the destructive action enables. Confirm sends the `expected` token; the server 409s
 * (`MANUAL_STATS_STALE`) if the manual changed (rename / move / archive / category-rename) since — handled inline with a
 * Refresh. Other errors route to the shared error dialog.
 */
export function DeleteManualDialog({ manual, session, onClose, onDeleted }: DeleteManualDialogProps) {
  const { t } = useTranslation();
  const stats = useManualStats(session, manual.id);
  const del = useDeleteManual(session);
  const showError = useShowError();
  const [typed, setTyped] = useState("");

  // Authoritative (server) name once stats load; trimmed so an invisible trailing space in the data doesn't make it
  // un-typeable (mirrors DeleteFacilityDialog). The opaque `expected` token — not this type-to-confirm friction — is the
  // delete's INTEGRITY guard (it pins the exact manual id), so the trim is a deliberate typeability trade-off (panel #3).
  const confirmName = (stats.data?.name ?? manual.name).trim();
  const isStale = del.isError && (del.error as ManualMutationError | undefined)?.code === "MANUAL_STATS_STALE";
  // `!isStale` too: after a 409 the `expected` token is stale until Refresh refetches it — without this, once the
  // mutation settles the button re-enables and a re-click re-sends the SAME stale token in a 409 loop (panel #2).
  const canConfirm = stats.data != null && !stats.isFetching && typed === confirmName && !del.isPending && !isStale;

  const close = () => {
    del.reset();
    onClose();
  };
  const onConfirm = () => {
    if (!stats.data || del.isPending) return;
    del.mutate(
      { id: manual.id, expected: stats.data.expected },
      {
        onSuccess: () => {
          onDeleted(confirmName);
          close();
        },
        onError: (err) => {
          if ((err as ManualMutationError).code !== "MANUAL_STATS_STALE") {
            showError(err);
            close();
          }
        },
      },
    );
  };
  const onRefresh = () => {
    del.reset();
    setTyped("");
    void stats.refetch();
  };

  return (
    <Dialog isOpen isDismissable={!del.isPending} onOpenChange={(open) => !open && !del.isPending && close()}>
      <DialogTitle>{t("manualManagement.delete.title")}</DialogTitle>

      {stats.isPending ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("common.loading")}
        </p>
      ) : stats.isError ? (
        <>
          <DialogDescription>{statsErrorCopy(stats.error, t)}</DialogDescription>
          <DialogFooter>
            <Button onPress={close}>
              {t("manualManagement.misc.close")}
            </Button>
          </DialogFooter>
        </>
      ) : stats.data ? (
        <>
          <DialogDescription>
            <Trans
              i18nKey="manualManagement.delete.permanentlyFull"
              components={{
                name: <strong className="text-foreground">{confirmName}</strong>,
                cat: <span className="text-foreground">{stats.data.categoryName}</span>,
              }}
            />
          </DialogDescription>
          <p className="text-destructive text-sm font-medium">{t("manualManagement.delete.cannotUndo")}</p>
          {stats.data.linkedChildCount > 0 ? (
            <p className="text-destructive text-sm">
              {t("manualManagement.delete.alsoDeletes")}{" "}
              {t("manualManagement.delete.linkedCopies", { count: stats.data.linkedChildCount })}
            </p>
          ) : null}

          <TextField value={typed} onChange={setTyped} className="flex flex-col gap-1.5" isDisabled={del.isPending || stats.isFetching}>
            <Label className="text-sm">
              <Trans
                i18nKey="manualManagement.delete.typeToConfirm"
                components={{ name: <strong className="text-foreground">{confirmName}</strong> }}
              />
            </Label>
            <Input autoFocus placeholder={confirmName} className="bg-background" />
          </TextField>

          {isStale ? (
            <div role="alert" className="text-destructive flex flex-col items-start gap-2 text-sm">
              <span className="flex items-center gap-1.5">
                <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
                {t("manualManagement.delete.stale")}
              </span>
              <Button variant="outline" size="sm" onPress={onRefresh}>
                {t("common.refresh")}
              </Button>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onPress={close} isDisabled={del.isPending}>
              {t("other.cancelButton")}
            </Button>
            <Button variant="destructive" onPress={onConfirm} isDisabled={!canConfirm}>
              {del.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  {t("manualManagement.delete.deleting")}
                </>
              ) : (
                t("manualManagement.delete.confirm")
              )}
            </Button>
          </DialogFooter>
        </>
      ) : null}
    </Dialog>
  );
}

function statsErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as ManualMutationError | undefined)?.code;
  if (code === "MANUAL_FORBIDDEN") return t("manualManagement.delete.forbidden");
  if (code === "MANUAL_NOT_FOUND") return t("manualManagement.delete.gone");
  return t("manualManagement.delete.loadFailed");
}
